import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button, FormLabel, Typography } from "@material-ui/core";

import ContentNutritionRecipesStore from "stores/ContentNutritionRecipesStore";
import NutritionCategoryDictionary from "stores/dictionary/NutritionCategoryDictionary";
import TagDictionary from "stores/dictionary/TagDictionary";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import TagsControl from "components/TagsControl";
import { SNACKBAR_TYPES } from "components/Snackbar";
import SimpleField from "components/SimpleField";

import IngredientsControl from "./IngredientsControl";
import MacroNutrientsControl from "./MacroNutrientsControl";
import DurationFieldControl from "./DurationFieldControl";

import { PUBLISH_STATUSES, STRUCTURE_TYPES } from "constants/models";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { getDurationSeconds } from "utils/calculation";
import { getNutrientLabel, getUnits } from "utils/nutrients";
import {
  compose,
  fileSizeValidator,
  imageIsCorruptedValidator,
  imageTypesValidator,
  lengthValidator,
  recipeDurationValidator,
} from "utils/validators";

import {
  BackButtonWrapper,
  Container,
  TaggingButtonContainer,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  GroupRowLabelContainer,
  Row,
  Divider,
  UploadPreviewFieldControl,
  ImagePreviewContainer,
  ValuesWrapper,
  ValueContainer,
  PublishStatusWrapper,
} from "./styles";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<
  { recipeId: string; id?: string },
  {},
  LocationState
> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

export const MACRONUTRIENTS_KEYS = {
  CARBS: "carbs",
  FIBER: "fiber",
  FAT: "fat",
  PROTEIN: "protein",
  CALORIES: "calories",
  SUGAR: "sugar",
  SATURATEDFAT: "saturatedFat",
  TRANSFAT: "transFat",
  CHOLESTEROL: "cholesterol",
  SODIUM: "sodium",
  VITAMIN_A: "vitaminA",
  VITAMIN_C: "vitaminC",
  CALCIUM: "calcium",
  IRON: "iron",
};

export const MACRONUTRIENTS_REQUIRED_KEYS = [
  MACRONUTRIENTS_KEYS.CARBS,
  MACRONUTRIENTS_KEYS.FAT,
  MACRONUTRIENTS_KEYS.PROTEIN,
  MACRONUTRIENTS_KEYS.FIBER,
];

export const MACRONUTRIENTS_UNIT_GRAMS_KEYS = [
  MACRONUTRIENTS_KEYS.CARBS,
  MACRONUTRIENTS_KEYS.FAT,
  MACRONUTRIENTS_KEYS.PROTEIN,
  MACRONUTRIENTS_KEYS.FIBER,
  MACRONUTRIENTS_KEYS.SUGAR,
  MACRONUTRIENTS_KEYS.SATURATEDFAT,
  MACRONUTRIENTS_KEYS.TRANSFAT,
];

@observer
class RecipeDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable isConfirmationPopupOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewRecipe() {
    const { recipeId } = this.props.match.params;

    return recipeId === undefined;
  }

  get recipeId() {
    const { recipeId } = this.props.match.params;

    const parsedRecipeId = Number.parseInt(recipeId);
    return typeof parsedRecipeId === "number" ? parsedRecipeId : undefined;
  }

  get categoryId() {
    const { id } = this.props.match.params;

    const categoryId = id ? Number.parseInt(id) : undefined;
    return typeof categoryId === "number" ? categoryId : undefined;
  }

  get backClickText() {
    return this.categoryId ? "Back to Category Details" : " Back to Recipes";
  }

  get backClickPath() {
    return this.categoryId
      ? generatePath(ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS, {
          id: this.categoryId,
        })
      : ROUTES.CONTENT_NUTRITION_RECIPES;
  }

  @computed get categoryOptions() {
    const weekCategoryOptions =
      this.initialValues.category
        ?.filter((item: any) => item.structureType === STRUCTURE_TYPES.WEEK)
        .map((item: any) => ({
          value: item,
          label: item.name,
          isFixed: true,
          isDisabled: true,
        })) ?? [];

    return [...NutritionCategoryDictionary.options, ...weekCategoryOptions];
  }

  @computed get displayValues(): any {
    const formData = ContentNutritionRecipesStore.details?.formData;
    const durationText = ContentNutritionRecipesStore.details?.durationText;

    return {
      ...formData,
      category: formData?.category?.map(({ name }) => name).join(", "),
      tags: formData?.tags?.map(
        ({ id, title }: { id: number; title: string }) => ({ id, name: title }),
      ),
      durationText,
      claims:
        formData?.claims?.map(({ id, title }) => ({ id, name: title })) || [],
      restrictions:
        formData?.restrictions?.map(({ id, title }) => ({
          id,
          name: title,
        })) || [],
      healthConditions:
        formData?.healthConditions?.map(({ id, title }) => ({
          id,
          name: title,
        })) || [],
    };
  }

  @computed get initialValues(): any {
    if (this.isNewRecipe) {
      return {
        durationHoursMins: { hours: "", minutes: "" },
        ingredients: [{ title: "", unit: "" }],
      };
    } else {
      return {
        durationHoursMins: { hours: "", minutes: "" },
        ...ContentNutritionRecipesStore.details?.formData,
      };
    }
  }

  @computed get recipePublishStatus() {
    return ContentNutritionRecipesStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    Promise.all([TagDictionary.fetch(), NutritionCategoryDictionary.fetch()]);

    if (this.isNewRecipe) {
      history.replace({ state: { editMode: true } });
    } else {
      await ContentNutritionRecipesStore.fetchRecipeDetails(this.recipeId);
      await ContentNutritionRecipesStore.fetchRecipeTagging(this.recipeId);
    }
  }

  componentWillUnmount() {
    ContentNutritionRecipesStore.resetDetails();
  }

  getMacronutrientText = (key: string) => {
    const countLabel = getUnits(key);
    return this.displayValues?.macronutrients[key] !== undefined
      ? `${this.displayValues?.macronutrients[key]} ${countLabel}`
      : "";
  };

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.isConfirmationPopupOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.isConfirmationPopupOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewRecipe) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
      recipeId: this.recipeId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
      recipeId: this.recipeId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.recipePublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.recipePublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const {
      file,
      id,
      category,
      durationHoursMins,
      instruction,
      title,
      ingredients,
      macronutrients,
      tags,
    } = values;

    const data = {
      categoryIds: category
        ?.filter(
          ({ structureType }: { structureType: string }) =>
            structureType !== STRUCTURE_TYPES.WEEK,
        )
        .map(({ id }: { id: number }) => id),
      instruction: instruction.trim(),
      title: title.trim(),
      tagIds: tags?.map(({ id }: { id: number }) => id),
      duration: getDurationSeconds({
        hours: durationHoursMins.hours,
        minutes: durationHoursMins.minutes,
        seconds: "0",
      }),
      macronutrients,
      ingredients,
    };

    const payload = { file, id, data: JSON.stringify(data) };

    try {
      if (this.isNewRecipe) {
        const id = await ContentNutritionRecipesStore.createRecipe(payload);
        if (id) {
          // await this.onTaggingGeneration();
          const path = generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
            recipeId: id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          await ContentNutritionRecipesStore.fetchRecipeDetails(id);
          // await ContentNutritionRecipesStore.fetchRecipeTagging(id);
        }
      } else {
        const payload = { ...data, id };
        await ContentNutritionRecipesStore.editRecipeInfo(payload);
        // await this.onTaggingGeneration();
        if (typeof file !== "string") {
          const payload = { file, id };
          await ContentNutritionRecipesStore.editRecipeImage(payload);
        }

        this.exitEditMode();
        await ContentNutritionRecipesStore.fetchRecipeDetails(id);
        // await ContentNutritionRecipesStore.fetchRecipeTagging(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onTaggingGeneration = async () => {
    const { setMessage } = this.props;
    const payload = { id: this.recipeId };

    try {
      const success = await ContentNutritionRecipesStore.generateTagging(
        payload,
      );
      if (!success) {
        throw new Error("Error while generating tags, try again later.");
      }
      setMessage("Tagging Done!!", SNACKBAR_TYPES.SUCCESS);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    } finally {
      await ContentNutritionRecipesStore.fetchRecipeDetails(this.recipeId);
      await ContentNutritionRecipesStore.fetchRecipeTagging(this.recipeId);
    }
  };

  render() {
    return (
      <Page
        title="Recipe Details"
        leftControls={
          this.recipePublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.recipePublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickText}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={ContentNutritionRecipesStore.isRecipeLoading}
      >
        {this.isConfirmationPopupOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          mutators={{ ...arrayMutators }}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewRecipe}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{ dirty: true }}
            onChange={this.onChangeIsPopupDirty}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column width="50%">
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="file"
                      acceptedFiles={ACCEPTED_IMAGE_TYPES}
                      validate={compose(
                        this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                        fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                        imageIsCorruptedValidator,
                      )}
                      required
                    />
                  ) : (
                    <ImagePreviewContainer
                      image={this.displayValues?.imageUrl}
                    />
                  )}
                </Row>
              </Column>

              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DurationFieldControl
                        name="durationHoursMins"
                        validate={recipeDurationValidator}
                      />
                    ) : (
                      <SimpleField
                        label="Duration"
                        value={this.displayValues?.durationText}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="category"
                        options={this.categoryOptions}
                        label="Category"
                        multi
                        isClearable
                      />
                    ) : (
                      <SimpleField
                        label="Category"
                        value={this.displayValues?.category}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Row>
                    <TagsControl name="tags" options={TagDictionary.options} />
                  </Row>
                ) : (
                  <SimpleField label="Tags" value={this.displayValues?.tags} />
                )}
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Row>
                    <IngredientsControl name="ingredients" />
                  </Row>
                ) : (
                  <>
                    <GroupRowLabelContainer>
                      <FormLabel>Ingredients</FormLabel>
                    </GroupRowLabelContainer>
                    <ValuesWrapper>
                      {this.displayValues?.ingredients?.map(
                        (ingredient: any, index: number) => (
                          <ValueContainer key={`ingredientKey:${index}`}>
                            <Column width="70%">
                              <Typography variant="subtitle1">
                                {ingredient.title}
                              </Typography>
                            </Column>
                            <Column width="30%" alignTextRight>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                              >
                                {ingredient.unit}
                              </Typography>
                            </Column>
                          </ValueContainer>
                        ),
                      )}
                    </ValuesWrapper>
                  </>
                )}
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      name="instruction"
                      variant="outlined"
                      margin="none"
                      label="Instructions"
                      rows={5}
                      rowsMax={10}
                      disabled={!this.isEditMode}
                      fullWidth
                      multiline
                      required
                    />
                  ) : (
                    <SimpleField
                      label="Instructions"
                      value={this.displayValues?.instruction}
                      linesCount={25}
                    />
                  )}
                </Row>
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Row>
                    <MacroNutrientsControl name="macronutrients" />
                  </Row>
                ) : (
                  <>
                    <GroupRowLabelContainer>
                      <FormLabel>Nutrition Facts</FormLabel>
                    </GroupRowLabelContainer>
                    <ValuesWrapper>
                      {this.displayValues?.macronutrients &&
                        Object.values(MACRONUTRIENTS_KEYS).map(
                          (key: string, index: number) => (
                            <ValueContainer key={`macronutrientKey:${index}`}>
                              <Column width="70%">
                                <Typography variant="subtitle1">
                                  {getNutrientLabel(key)}
                                </Typography>
                              </Column>
                              <Column width="30%" alignTextRight>
                                <Typography
                                  variant="subtitle1"
                                  color="textSecondary"
                                >
                                  {this.getMacronutrientText(key)}
                                </Typography>
                              </Column>
                            </ValueContainer>
                          ),
                        )}
                    </ValuesWrapper>
                  </>
                )}
              </Column>
            </GroupRow>

            {/* <Divider />

            <GroupRow>
              <Column>
                <SimpleField
                  label="Claims"
                  value={this.displayValues?.claims}
                />
              </Column>
            </GroupRow>

            <GroupRow>
              <Column>
                <SimpleField
                  label="Restrictions"
                  value={this.displayValues?.restrictions}
                />
              </Column>
            </GroupRow>

            <GroupRow>
              <Column>
                <SimpleField
                  label="Health Condition"
                  value={this.displayValues?.healthConditions}
                />
              </Column>
            </GroupRow>

            <TaggingButtonContainer>
              <ControlContainer>
                <LoadingButton
                  onClick={this.onTaggingGeneration}
                  variant="contained"
                  color="primary"
                  isLoading={
                    ContentNutritionRecipesStore.isTagGenerationLoading
                  }
                  fullWidth
                >
                  Tagging Generation
                </LoadingButton>
              </ControlContainer>
            </TaggingButtonContainer> */}
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ContentNutritionRecipesStore.isRecipeManaging}
      fullWidth
    >
      {this.isNewRecipe ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(RecipeDetails));
